import type { WorkflowRecipe } from "@/lib/workflows/recipes";

export type WorkflowCatalogId = "growth" | "bookings" | "care" | "custom";

export interface WorkflowCatalog {
  id: WorkflowCatalogId;
  title: string;
  description: string;
}

export const WORKFLOW_CATALOGS: WorkflowCatalog[] = [
  {
    id: "growth",
    title: "Grow customers",
    description: "Follow up with new leads and first-time customers.",
  },
  {
    id: "bookings",
    title: "Protect bookings",
    description: "Confirm attendance and help missed bookings return.",
  },
  {
    id: "care",
    title: "Customer care",
    description: "Handle complaints and sensitive moments thoughtfully.",
  },
  {
    id: "custom",
    title: "My workflows",
    description: "Manage the workflows your team has created.",
  },
];

export const DEFAULT_WORKFLOW_CATALOG: Record<string, Exclude<WorkflowCatalogId, "custom">> = {
  "trial-to-paid": "growth",
  "idle-lead-winback": "growth",
  "new-lead-qualification": "growth",
  "attendance-confirmation": "bookings",
  "no-show-recovery": "bookings",
  "complaint-recovery": "care",
  "crisis-outreach-protection": "care",
};

export const DEFAULT_WORKFLOW_CARD_COPY: Record<string, string> = {
  "trial-to-paid":
    "Invite first-time customers back while their visit is still fresh.",
  "idle-lead-winback":
    "Follow up once when a new enquiry goes quiet.",
  "new-lead-qualification":
    "Understand new enquiries and keep promising leads moving towards becoming customers.",
  "attendance-confirmation":
    "Ask customers to confirm upcoming bookings and alert your team when they do not reply.",
  "no-show-recovery":
    "Send one friendly rebooking message after a missed appointment.",
  "complaint-recovery":
    "Bring new complaints into one place so your team can respond quickly.",
  "crisis-outreach-protection":
    "Pause sales messages when a customer needs personal attention.",
};

/**
 * Templates exposed in the customer-facing catalog.
 *
 * Keep this as a UI allowlist so the underlying workflow definitions remain
 * available to existing workflows and backend execution.
 */
const VISIBLE_WORKFLOW_TEMPLATE_IDS: ReadonlySet<string> = new Set([
  "idle-lead-winback",
  "new-lead-qualification",
  // Requested by the owner (Oct 2026), alongside Google review links.
  "first-session-feedback",
]);

export function isWorkflowTemplateVisible(templateId: string): boolean {
  return VISIBLE_WORKFLOW_TEMPLATE_IDS.has(templateId);
}

export type WorkflowLifecycleBucket = "used" | "enabled" | "available";

/**
 * A workflow remains part of "Your workflows" after its first execution,
 * even if the business later pauses it. This keeps its audit trail easy to
 * find and avoids moving historical workflows back into the available list.
 */
export function getWorkflowLifecycleBucket({
  enabled,
  runCount,
}: {
  enabled: boolean;
  runCount: number;
}): WorkflowLifecycleBucket {
  if (runCount > 0) return "used";
  return enabled ? "enabled" : "available";
}

export interface TemplateSection {
  id: WorkflowRecipe["outcome"];
  title: string;
  description: string;
}

export const TEMPLATE_SECTIONS: TemplateSection[] = [
  {
    id: "convert",
    title: "Grow & follow up",
    description: "Continue promising conversations without adding pressure.",
  },
  {
    id: "care",
    title: "Care check-ins",
    description: "Stay thoughtful after a customer receives a service.",
  },
  {
    id: "learn",
    title: "Feedback",
    description: "Learn what customers enjoyed and where you can improve.",
  },
  {
    id: "recover",
    title: "Win customers back",
    description: "Offer an easy next step after a cancellation or resolved issue.",
  },
];

export const TEMPLATE_CARD_COPY: Record<string, string> = {
  "first-session-feedback":
    "Ask how a customer's first session went, save their answer to their profile, and invite them to leave a Google review.",
  "clinic-consultation-follow-up":
    "Send one friendly follow-up after a first visit or quote.",
  "clinic-post-treatment-check-in":
    "Check in after a completed service, with your team reviewing the message first.",
  "clinic-private-experience-feedback":
    "Ask customers privately how their experience went.",
  "clinic-case-recovery-reminder":
    "Remind your team to check in after a customer issue is resolved.",
  "ktv-cancellation-recovery":
    "Offer an easy way to choose another date or room after a cancellation.",
  "ktv-post-visit-feedback":
    "Ask for quick private feedback while the visit is still fresh.",
};
